import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-40 bg-[rgba(20,16,8,0.40)] backdrop-blur-[2px]',
      'data-[state=open]:animate-fade-in',
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = 'SheetOverlay';

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: 'right' | 'left';
  width?: string;
}

export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, children, side = 'right', width = 'w-[min(92vw,560px)]', ...props }, ref) => (
  <DialogPrimitive.Portal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-50 top-0 h-dvh flex flex-col',
        'bg-card border-card-stroke shadow-clay',
        side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
        side === 'right'
          ? 'data-[state=open]:animate-slide-in-right'
          : 'data-[state=open]:animate-fade-in',
        width,
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
SheetContent.displayName = 'SheetContent';

export function SheetHeader({
  title,
  description,
  eyebrow,
}: {
  title: string;
  description?: string;
  eyebrow?: string;
}) {
  return (
    <header className="flex items-start justify-between gap-4 border-b border-card-stroke px-6 py-5">
      <div>
        {eyebrow && (
          <span className="block font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">
            {eyebrow}
          </span>
        )}
        <DialogPrimitive.Title className="font-display text-[22px] font-bold tracking-[-0.02em] text-fg leading-[1.1] mt-0.5">
          {title}
        </DialogPrimitive.Title>
        {description && (
          <DialogPrimitive.Description className="text-sm text-fg-muted mt-1">
            {description}
          </DialogPrimitive.Description>
        )}
      </div>
      <SheetClose
        aria-label="Fechar"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-2 text-fg-muted hover:text-fg hover:bg-card-soft transition-colors"
      >
        <X className="h-4 w-4" strokeWidth={1.8} />
      </SheetClose>
    </header>
  );
}

export function SheetBody({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('flex-1 overflow-y-auto px-6 py-5', className)}>{children}</div>;
}

export function SheetFooter({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <footer
      className={cn(
        'flex items-center justify-end gap-2 border-t border-card-stroke px-6 py-4 bg-card-soft',
        className,
      )}
    >
      {children}
    </footer>
  );
}
